import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, DollarSign, Truck, Users, Loader2 } from 'lucide-react';

export default function DispatcherAnalytics({ user }) {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/dispatcher-analytics')
      .then(res => setAnalytics(res.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (user.role !== 'super_admin' && user.role !== 'broker') {
    return <p className="text-center text-slate-500 py-12">Broker or Super Admin access only.</p>;
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const totals = analytics.reduce((acc, d) => ({
    loads: acc.loads + d.total_loads,
    revenue: acc.revenue + d.total_revenue,
    cost: acc.cost + d.total_carrier_cost,
    profit: acc.profit + d.total_profit
  }), { loads: 0, revenue: 0, cost: 0, profit: 0 });

  const maxProfit = Math.max(...analytics.map(d => d.total_profit), 1);

  return (
    <div className="space-y-6" data-testid="dispatcher-analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dispatcher Analytics</h1>
        <p className="text-slate-600 mt-1">Track dispatcher performance, loads booked, and margins</p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <Truck className="w-6 h-6 mx-auto text-blue-600 mb-1" />
            <p className="text-2xl font-bold">{totals.loads}</p>
            <p className="text-xs text-slate-500">Total Loads</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <DollarSign className="w-6 h-6 mx-auto text-green-600 mb-1" />
            <p className="text-2xl font-bold">${totals.revenue.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Total Revenue</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <DollarSign className="w-6 h-6 mx-auto text-red-600 mb-1" />
            <p className="text-2xl font-bold">${totals.cost.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Carrier Costs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
            <p className="text-2xl font-bold text-emerald-600">${totals.profit.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Total Profit</p>
          </CardContent>
        </Card>
      </div>

      {/* Dispatcher Table */}
      {analytics.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium text-slate-500">No dispatchers with loads yet</p>
            <p className="text-sm text-slate-400 mt-1">Agents need to book loads to show up here</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Performance by Dispatcher
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-table">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500 uppercase">
                    <th className="pb-2 pr-4">Dispatcher</th>
                    <th className="pb-2 pr-4">PIN</th>
                    <th className="pb-2 pr-4 text-right">Loads</th>
                    <th className="pb-2 pr-4 text-right">Active</th>
                    <th className="pb-2 pr-4 text-right">Revenue</th>
                    <th className="pb-2 pr-4 text-right">Carrier Cost</th>
                    <th className="pb-2 pr-4 text-right">Profit</th>
                    <th className="pb-2 pr-4 text-right">Margin</th>
                    <th className="pb-2 pr-4 text-right">Avg $/Mi</th>
                    <th className="pb-2 pr-4">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map(d => (
                    <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`dispatcher-row-${d.pin}`}>
                      <td className="py-3 pr-4 font-medium">{d.name}</td>
                      <td className="py-3 pr-4 text-slate-500">{d.pin}</td>
                      <td className="py-3 pr-4 text-right">{d.total_loads}</td>
                      <td className="py-3 pr-4 text-right">{d.active_loads}</td>
                      <td className="py-3 pr-4 text-right">${d.total_revenue.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right text-red-600">${d.total_carrier_cost.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right font-bold text-emerald-600">${d.total_profit.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          d.avg_margin >= 20 ? 'bg-green-100 text-green-700' :
                          d.avg_margin >= 10 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>{d.avg_margin}%</span>
                      </td>
                      <td className="py-3 pr-4 text-right">${d.avg_per_mile}/mi</td>
                      <td className="py-3 pr-4">
                        <div className="w-24 bg-slate-100 rounded-full h-2">
                          <div
                            className="bg-blue-600 rounded-full h-2 transition-all"
                            style={{ width: `${(d.total_profit / maxProfit) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
